import { useEffect, useMemo } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { QUERY_KEYS } from "@/lib/constants";
import type {
  Product,
  Category,
  Attribute,
  CreateProductRequest,
  UpdateProductRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateAttributeRequest,
  UpdateAttributeRequest,
  CreateAttributeOptionRequest,
  UpdateAttributeOptionRequest,
  CreateVariantRequest,
  UpdateVariantRequest,
  ProductVariant,
  PaginatedResponse,
  CategoryIngredient,
  SetCategoryIngredientsRequest,
} from "@/types";

interface ProductFilters {
  categoryId?: string;
  status?: "all" | "active" | "inactive";
  search?: string;
  page?: number;
  limit?: number;
  all?: boolean;
}

export function useProducts(params?: ProductFilters) {
  return useQuery({
    queryKey: [...QUERY_KEYS.products, params],
    queryFn: () =>
      apiClient.get<PaginatedResponse<Product>>("/products", {
        categoryId: params?.categoryId,
        status: params?.status,
        search: params?.search,
        page: params?.page,
        limit: params?.limit,
        all: params?.all,
      }),
    placeholderData: (prev) => prev,
  });
}

const PRODUCTS_PAGE_SIZE = 1000;

export function useAllProducts(params?: Omit<ProductFilters, "page" | "limit">) {
  const query = useInfiniteQuery({
    queryKey: [...QUERY_KEYS.allProducts, params],
    queryFn: ({ pageParam }) =>
      apiClient.get<PaginatedResponse<Product>>("/products", {
        ...params,
        page: pageParam,
        limit: PRODUCTS_PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: ({ pagination }) =>
      pagination.page < pagination.totalPages ? pagination.page + 1 : undefined,
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const products = useMemo(
    () => query.data?.pages.flatMap((page) => page.data),
    [query.data],
  );

  return { ...query, data: products };
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.product(id),
    queryFn: () => apiClient.get<Product>(`/products/${id}`),
    enabled: !!id,
  });
}

export function useCategories(params?: { all?: boolean }) {
  return useQuery({
    queryKey: [...QUERY_KEYS.categories, params],
    queryFn: () =>
      apiClient.get<Category[]>("/categories", { all: params?.all }),
  });
}

export function useCategory(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.category(id),
    queryFn: () => apiClient.get<Category>(`/categories/${id}`),
    enabled: !!id,
  });
}

export function useAttributes() {
  return useQuery({
    queryKey: QUERY_KEYS.attributes,
    queryFn: () => apiClient.get<Attribute[]>("/attributes"),
  });
}

export function useAttribute(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.attribute(id),
    queryFn: () => apiClient.get<Attribute>(`/attributes/${id}`),
    enabled: !!id,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProductRequest) =>
      apiClient.post<Product>("/products", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.products });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductRequest }) =>
      apiClient.put<Product>(`/products/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.products });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.product(id) });
    },
  });
}

export function useToggleProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.patch<Product>(`/products/${id}/toggle`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.products });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.product(id) });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/products/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.products });
    },
  });
}

export function useUpdateProductAttributes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      attributeIds,
    }: {
      id: string;
      attributeIds: string[];
    }) => apiClient.put(`/products/${id}/attributes`, { attributeIds }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.products });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.product(id) });
    },
  });
}

export function useAddVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      data,
    }: {
      productId: string;
      data: CreateVariantRequest;
    }) =>
      apiClient.post<ProductVariant>(`/products/${productId}/variants`, data),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.products });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.product(productId),
      });
    },
  });
}

export function useUpdateVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      variantId,
      data,
    }: {
      variantId: string;
      data: UpdateVariantRequest;
    }) =>
      apiClient.put<ProductVariant>(`/products/variants/${variantId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.products });
      queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useDeleteVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variantId: string) =>
      apiClient.delete(`/products/variants/${variantId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.products });
    },
  });
}

export function useVariantIngredients(variantId: string | null) {
  return useQuery({
    queryKey: [...QUERY_KEYS.products, "variant-ingredients", variantId],
    queryFn: () =>
      apiClient.get<{ ingredientId: string; quantityUsed: number }[]>(
        `/products/variants/${variantId}/ingredients`,
      ),
    enabled: !!variantId,
  });
}

export function useSetVariantIngredients() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      variantId,
      ingredients,
    }: {
      variantId: string;
      ingredients: { ingredientId: string; quantityUsed: number }[];
    }) =>
      apiClient.put(`/products/variants/${variantId}/ingredients`, {
        ingredients,
      }),
    onSuccess: (_, { variantId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.products });
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.products, "variant-ingredients", variantId],
      });
    },
  });
}

export function useOptionIngredients(optionId: string | null) {
  return useQuery({
    queryKey: [...QUERY_KEYS.attributes, "option-ingredients", optionId],
    queryFn: () =>
      apiClient.get<{ ingredientId: string; quantityUsed: number }[]>(
        `/attributes/options/${optionId}/ingredients`,
      ),
    enabled: !!optionId,
  });
}

export function useSetOptionIngredients() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      optionId,
      ingredients,
    }: {
      optionId: string;
      ingredients: { ingredientId: string; quantityUsed: number }[];
    }) =>
      apiClient.put(`/attributes/options/${optionId}/ingredients`, {
        ingredients,
      }),
    onSuccess: (_, { optionId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.attributes });
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.attributes, "option-ingredients", optionId],
      });
    },
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCategoryRequest) =>
      apiClient.post<Category>("/categories", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.categories });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCategoryRequest }) =>
      apiClient.put<Category>(`/categories/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.categories });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.category(id) });
    },
  });
}

export function useToggleCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.patch<Category>(`/categories/${id}/toggle`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.categories });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.category(id) });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/categories/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.categories });
    },
  });
}

export function useCategoryIngredients(categoryId: string) {
  return useQuery({
    queryKey: [...QUERY_KEYS.categories, categoryId, "ingredients"],
    queryFn: () =>
      apiClient.get<CategoryIngredient[]>(
        `/categories/${categoryId}/ingredients`,
      ),
    enabled: !!categoryId,
  });
}

export function useSetCategoryIngredients() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      categoryId,
      data,
    }: {
      categoryId: string;
      data: SetCategoryIngredientsRequest;
    }) =>
      apiClient.put<CategoryIngredient[]>(
        `/categories/${categoryId}/ingredients`,
        data,
      ),
    onSuccess: (_, { categoryId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.categories });
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.categories, categoryId, "ingredients"],
      });
    },
  });
}

export function useCreateAttribute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAttributeRequest) =>
      apiClient.post<Attribute>("/attributes", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.attributes });
    },
  });
}

export function useUpdateAttribute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAttributeRequest }) =>
      apiClient.put<Attribute>(`/attributes/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.attributes });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.attribute(id) });
    },
  });
}

export function useDeleteAttribute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/attributes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.attributes });
    },
  });
}

export function useCreateAttributeOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      attributeId,
      data,
    }: {
      attributeId: string;
      data: CreateAttributeOptionRequest;
    }) => apiClient.post(`/attributes/${attributeId}/options`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.attributes });
    },
  });
}

export function useUpdateAttributeOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      optionId,
      data,
    }: {
      optionId: string;
      data: UpdateAttributeOptionRequest;
    }) => apiClient.put(`/attributes/options/${optionId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.attributes });
      queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useDeleteAttributeOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (optionId: string) =>
      apiClient.delete(`/attributes/options/${optionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.attributes });
    },
  });
}
